import React from 'react';
import glamorous from 'glamorous';
import PropTypes from 'prop-types';
import {ASH, FOCUS_BLUE, SILVER_ALUMINIUM, WHITE, GUNSMOKE} from '../../../../shared/style/colors';
import Tags from '../../../../shared/library/tags';
import {PHONE, TABLET} from '../../../../shared/style/breakpoints';
import BigTitle from '../../../../shared/library/typography/big-title';
import Text from '../../../../shared/library/typography/text';
import SmallTitle from '../../../../shared/library/typography/small-title';
import {truncateText} from '../../../../shared/utils/truncate-text';
import FollowCompanyButton from '../../../../shared/library/buttons/follow/follow-company-button';
import LoadMoreButton from '../../../../shared/library/buttons/load-more';
import {useSendAnalyticsEvent} from '../../../../shared/enhancers/analytics-enhancer';
import {Query} from 'react-apollo';
import {Spinner} from '../../../tool-profile/components/shared/styles';
import Circular, {LARGE} from '../../../../shared/library/indicators/indeterminate/circular';
import {flattenEdges} from '../../../../shared/utils/graphql';
import {companies} from '../../../../data/company-profile/queries';
import {SigninDesktopModal} from '../../../../shared/library/modals/signin';
import VerfiedIcon from '../../../../shared/library/icons/verified.svg';
import {COMPANY} from '../../../../shared/constants/analytics';

const Container = glamorous.div({
  display: 'grid',
  margin: '0 0 25px 0',
  gap: '20px 20px',
  gridTemplate: 'repeat(1, 1fr) / repeat(3, 1fr)',
  gridAutoRows: '1fr',
  [TABLET]: {
    gridTemplate: 'repeat(1, 1fr) / repeat(2, 1fr)',
    padding: '0 15px'
  },
  [PHONE]: {
    gridTemplate: 'repeat(1, 1fr) / repeat(1, 1fr)',
    gap: '25px 0',
    padding: '0 15px'
  }
});

const OuterCompanyContainer = glamorous.div({
  border: `1px solid ${ASH}`,
  borderRadius: 2,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  background: WHITE
});

const UpgradeLink = glamorous.div({
  display: 'block',
  textAlign: 'center',
  padding: '10px 0 25px 0',
  ' a': {
    color: GUNSMOKE,
    ' :hover': {
      color: FOCUS_BLUE
    }
  }
});

const CompanyContainer = glamorous.div({
  padding: '20px',
  position: 'relative'
});

const PopularTools = glamorous.div();

const Center = glamorous.div({
  textAlign: 'center',
  flexDirection: 'column',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
});

const Logo = glamorous.img({
  width: 80,
  height: 80,
  marginBottom: 10,
  borderRadius: 4,
  border: '1px solid #ebebeb'
});

const BtnContainer = glamorous.div({
  margin: '15px 0 10px 0',
  display: 'grid',
  gap: '0 15px',
  gridTemplate: 'repeat(1, 1fr) / repeat(2, 1fr)',
  gridAutoRows: '1fr',
  [PHONE]: {
    gridTemplate: 'repeat(1, 1fr) / repeat(1, 1fr)',
    gap: '15px 0'
  },
  ' button': {
    width: '100%'
  }
});

const GhostLink = glamorous.a({
  backgroundColor: 'transparent',
  borderRadius: 2,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: FOCUS_BLUE,
  ':hover': {
    borderColor: FOCUS_BLUE,
    background: FOCUS_BLUE,
    color: WHITE
  },
  border: `1px solid ${FOCUS_BLUE}`,
  outline: 'none'
});

const StyledTags = glamorous.div({
  minHeight: 80,
  display: 'flex',
  justifyContent: 'center',
  '> div': {
    justifyContent: 'center'
  }
});

const Description = glamorous(Text)({
  minHeight: 66
});

const StyledSmallTitle = glamorous(SmallTitle)({
  textTransform: 'uppercase',
  color: SILVER_ALUMINIUM,
  margin: '10px 0 15px 0'
});

const Title = glamorous.a({
  display: 'flex',
  alignItems: 'center'
});

const Verified = glamorous.div({
  marginLeft: 7
});

const ServiceImg = glamorous.img({
  width: 40,
  height: 40,
  border: `1px solid ${ASH}`
});

const ServicesContainer = glamorous.div({
  gridTemplateColumns: 'repeat(auto-fill, 40px)',
  [TABLET]: {
    gridTemplateColumns: 'repeat(auto-fill, 40px)'
  },
  justifyContent: 'space-between',
  gridGap: 10,
  display: 'grid',
  textAlign: 'center'
});

const OpenPositions = glamorous.div({
  background: '#fbb700',
  lineHeight: '25px',
  height: 25,
  padding: '0px 20px',
  textAlign: 'center',
  fontWeight: 'bold',
  ' a': {
    color: WHITE,
    ' &:hover': {
      color: WHITE
    }
  }
});

const updateQuery = (prev, next) => {
  if (next.companies.edges.length) {
    next.companies.edges = [...prev.companies.edges, ...next.companies.edges];
    return next;
  } else {
    return prev;
  }
};

const CompanyCard = ({signin, privateMode}) => {
  const sendAnalyticsEvent = useSendAnalyticsEvent();
  return (
    <Query
      query={companies}
      variables={{first: 30, after: null}}
      notifyOnNetworkStatusChange={true}
    >
      {({loading, data, fetchMore}) => {
        const hasData = data && data.companies;
        if (loading && !hasData)
          return (
            <Spinner>
              <Circular size={LARGE} />
            </Spinner>
          );

        if (hasData) {
          const companies = flattenEdges(data.companies);
          return (
            <>
              <UpgradeLink>
                <a
                  href="mailto:premium@stackshare.io?subject=Upgrade my company profile"
                  title="Get your company listed here"
                >
                  Get your company listed here
                </a>
              </UpgradeLink>
              <Container>
                {companies.map(c => (
                  <OuterCompanyContainer key={c.id}>
                    <CompanyContainer>
                      <Center>
                        <a href={c.path} title={c.name}>
                          <Logo src={c.imageUrl} alt={c.name} />
                        </a>
                        <Title href={c.path} title={c.name}>
                          <BigTitle>{c.name}</BigTitle>
                          {c.verified && (
                            <Verified>
                              <VerfiedIcon />
                            </Verified>
                          )}
                        </Title>
                        <Description>
                          {c.description && truncateText(c.description, 125, '...')}
                        </Description>
                      </Center>
                      <BtnContainer>
                        <FollowCompanyButton
                          companyId={c.id}
                          following={c.following}
                          onToggle={() => {
                            sendAnalyticsEvent('click_followButton', {
                              follow: !c.following,
                              followType: COMPANY
                            });
                          }}
                        />
                        <GhostLink
                          href={c.path}
                          title={c.name}
                          onClick={() =>
                            sendAnalyticsEvent('click_companyCard', {
                              companyName: c.name,
                              slug: c.slug
                            })
                          }
                        >
                          View
                        </GhostLink>
                      </BtnContainer>
                      {c.tags && (
                        <StyledTags>
                          <Tags tags={c.tags} disableLinks={true} />
                        </StyledTags>
                      )}
                      {c.popularTools && (
                        <PopularTools>
                          <StyledSmallTitle>Popular tools in stack</StyledSmallTitle>
                          <ServicesContainer>
                            {c.popularTools.edges.map(s => (
                              <a key={s.node.id} href={s.node.slug} title={s.node.name}>
                                <ServiceImg src={s.node.thumbUrl} alt={s.node.name} />
                              </a>
                            ))}
                          </ServicesContainer>
                        </PopularTools>
                      )}
                    </CompanyContainer>
                    {c.jobs.count !== 0 && !privateMode && (
                      <OpenPositions>
                        <a href={`${c.path}#jobs`}>
                          {c.jobs.count} Open {c.jobs.count === 1 ? 'Position' : 'Positions'}
                        </a>
                      </OpenPositions>
                    )}
                  </OuterCompanyContainer>
                ))}
              </Container>
              {data.companies.pageInfo.hasNextPage && (
                <Center>
                  <LoadMoreButton
                    loading={loading}
                    onClick={() =>
                      fetchMore({
                        variables: {
                          first: 30,
                          after: data.companies.pageInfo.endCursor
                        },
                        updateQuery: (prev, {fetchMoreResult: next}) => {
                          return updateQuery(prev, next);
                        }
                      })
                    }
                  />
                </Center>
              )}
              {signin && <SigninDesktopModal redirect="/companies" />}
            </>
          );
        } else {
          return null;
        }
      }}
    </Query>
  );
};

CompanyCard.propTypes = {
  signin: PropTypes.bool,
  privateMode: PropTypes.any
};

export default CompanyCard;
